/**
 * 그리드 셀 렌더링 헬퍼.
 *
 * - 값 + 타입을 받아 HTML 문자열로 반환.
 * - HTML 인젝션 방지를 위해 일반 텍스트는 모두 escape.
 * - 객체(JSON)는 type이 명시되지 않아도 자동으로 'json' 으로 처리.
 */

import type { GridColumn } from '../types';

export function escapeHtml(s: unknown): string {
  if (s == null) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * 카탈로그 검색 결과 한 줄의 **표시용 디스크립션**.
 *
 * 두 군데를 봐야 한다:
 *   등록 품목        → `description_raw` (제조사 원문)
 *   미등록(pending)  → `extra_description`
 *
 * pending 행은 카탈로그 테이블이 아니라 `catalog_part_pending` 에서 오므로
 * `description_raw` 가 **아예 NULL** 이다. 백엔드가 `extras.description` 으로
 * 실어 보내고 프론트에서 `extra_description` 으로 펼쳐진다
 * (search.queries.ts 의 pending 서브쿼리 주석 참조).
 *
 * 한쪽만 보면 미등록 품목이 검색 결과에서 **빈칸**으로 나온다. 같은 폴백이
 * 화면마다 복붙돼 있다가 서버 구성 쪽에서 빠져 실제로 그 증상이 났다.
 */
export function catalogHitDescription(
  hit: { description_raw?: string | null } & Record<string, unknown>,
): string {
  const raw = hit.description_raw;
  if (raw != null && String(raw).trim() !== '') return String(raw);
  const extra = hit['extra_description'];
  return extra == null ? '' : String(extra);
}

export function fmtNumber(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return v.toLocaleString();
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}

/**
 * 한국 시간 (KST = UTC+9) 으로 표시.
 *
 * 백엔드는 timestamptz 를 ISO 8601 'Z' (UTC) 로 직렬화 →
 * 그대로 표시하면 사용자 입장에서 9시간 어긋남.
 *
 * Intl.DateTimeFormat + timeZone 'Asia/Seoul' 로 강제. 사용자 브라우저가
 * 어디에 있든 한국 시간 (영업 기준) 으로 보여야 함 — B2B 시스템.
 *
 * en-CA 는 dateStyle 가 ISO 형식 'YYYY-MM-DD'. formatToParts 로 24h 강제.
 */
const KST_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year:  'numeric',
  month: '2-digit',
  day:   '2-digit',
});

const KST_DATETIME_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone:  'Asia/Seoul',
  year:      'numeric',
  month:     '2-digit',
  day:       '2-digit',
  hour:      '2-digit',
  minute:    '2-digit',
  hourCycle: 'h23',          // 00-23 (h12 가 '24:00' 토해내는 환경 회피)
});

export function fmtDate(v: unknown): string {
  if (!v) return '';
  try {
    const d = new Date(v as string | number);
    if (Number.isNaN(d.getTime())) return String(v);
    return KST_DATE_FMT.format(d);   // 'YYYY-MM-DD' (KST)
  } catch {
    return String(v);
  }
}

export function fmtDateTime(v: unknown): string {
  if (!v) return '';
  try {
    const d = new Date(v as string | number);
    if (Number.isNaN(d.getTime())) return String(v);
    // formatToParts 로 각 토큰 모아서 'YYYY-MM-DD HH:mm' 조립
    // (formatRange/format 의 separator 가 locale 별 ', ' / ' ' 다르게 나옴 회피)
    const parts = KST_DATETIME_FMT.formatToParts(d);
    const get = (type: string): string =>
      parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`;
  } catch {
    return String(v);
  }
}

function chipClassFor(value: unknown): string {
  const s = String(value ?? '').toLowerCase();
  if (['required', 'bto', 'cto', 'hpe'].includes(s)) return 'chip chip-primary';
  if (['optional', 'default'].includes(s)) return 'chip chip-muted';
  if (/^gen1[12]$/.test(s)) return 'chip chip-primary';
  if (['resolved', 'updated_new', 'verified_same'].includes(s)) return 'chip chip-success';
  if (['unresolved', 'kept_old', 'pdf_typo'].includes(s)) return 'chip chip-warning';
  return 'chip';
}

/**
 * 셀 1개 렌더 — 값과 컬럼 타입에 따라 HTML 문자열 반환.
 *
 * 객체(`typeof v === 'object'`이고 배열 아님)는 컬럼 타입과 무관하게
 * JSON 칩으로 표시 (드로어에서 상세 보기).
 */
export function renderCell(
  value: unknown,
  type: GridColumn['type'],
): string {
  if (value == null || value === '') {
    return '<span class="cell-empty">—</span>';
  }

  // 객체 자동 감지
  if (typeof value === 'object' && !Array.isArray(value)) {
    return '<span class="chip chip-json">JSON ▾</span>';
  }

  switch (type) {
    case 'mono':
      return `<span class="cell-mono">${escapeHtml(value)}</span>`;
    case 'num':
      return `<span class="cell-numeric">${escapeHtml(fmtNumber(value))}</span>`;
    case 'bool':
      if (value === true || value === 'true' || value === 't') {
        return '<span class="cell-bool true">✓</span>';
      }
      return '<span class="cell-bool false">✗</span>';
    case 'chip':
      return `<span class="${chipClassFor(value)}">${escapeHtml(value)}</span>`;
    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      if (arr.length === 0) return '<span class="cell-empty">—</span>';
      return arr.map((v) => `<span class="chip">${escapeHtml(v)}</span>`).join(' ');
    }
    case 'json':
      return '<span class="chip chip-json">JSON ▾</span>';
    case 'date':
      return escapeHtml(fmtDate(value));
    case 'datetime':
      return escapeHtml(fmtDateTime(value));
    case 'text':
    default:
      return escapeHtml(value);
  }
}

/**
 * 드로어용 값 렌더 — 셀보다 풍부하게.
 * 객체는 pre 블록으로 펼침, 배열은 칩.
 */
export function renderDrawerValue(v: unknown): string {
  if (v == null || v === '') return '<span class="cell-empty">—</span>';
  if (v === true || v === 'true') return '<span class="cell-bool true">✓ true</span>';
  if (v === false || v === 'false') return '<span class="cell-bool false">✗ false</span>';
  if (Array.isArray(v)) {
    if (v.length === 0) return '<span class="cell-empty">[]</span>';
    return v.map((x) => `<span class="chip">${escapeHtml(x)}</span>`).join(' ');
  }
  if (typeof v === 'object') {
    return `<pre>${escapeHtml(JSON.stringify(v, null, 2))}</pre>`;
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v)) {
    return escapeHtml(fmtDateTime(v));
  }
  return escapeHtml(v);
}

const KST_YYMMDD_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul', year: '2-digit', month: '2-digit', day: '2-digit',
});

/**
 * 견적서 다운로드 파일명 — `HCLT-견적서-[YYMMDD(KST)]-[업체명].xlsx`
 *   ex. HCLT-견적서-260625-젠틀몬스터.xlsx
 * 백엔드(quote-document.ts buildQuoteFilename)와 동일 규칙. 크로스오리진에서 브라우저가
 * Content-Disposition 을 못 읽어(Access-Control-Expose-Headers 미노출) fallback 되는 문제 때문에
 * 프론트가 가진 데이터(견적 생성일 + 딜 업체명)로 직접 생성한다.
 */
export function quoteFilename(
  createdAtIso: string | null | undefined,
  customerName: string | null | undefined,
  quoteId:      string | number,
): string {
  let date = '';
  if (createdAtIso) {
    const d = new Date(createdAtIso);
    if (!Number.isNaN(d.getTime())) {
      const p = KST_YYMMDD_FMT.formatToParts(d);
      const get = (t: string): string => p.find((x) => x.type === t)?.value ?? '';
      date = `${get('year')}${get('month')}${get('day')}`;
    }
  }
  const name = (customerName ?? '')
    .replace(/[/\\:*?"<>| -]/g, '')   // 파일명 금지문자·공백·하이픈 제거(백엔드와 동일)
    .trim();
  const segs = ['HCLT-견적서'];
  if (date) segs.push(date);
  segs.push(name || `quote-${quoteId}`);
  return `${segs.join('-')}.xlsx`;
}
